// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import { AccountAddress, TransactionResponseType } from "@aptos-labs/ts-sdk";

import { RPC_URLS } from "@/constants/chain-rpc";
import { isEvmChain } from "@/constants/chains";
import { readDexSdk } from "@/contexts/dex-sdk";
import { env } from "@/env";

import { APTOS_CCTP_DOMAIN_ID, getMessageTransmitterContract } from "./nonce/contracts";
import {
  appCctpNetwork,
  APTOS_MESSAGE_TRANSMITTER_V2,
  type CctpNetwork,
  EVM_TOKEN_MESSENGER_V2,
} from "./v2/contracts";

/**
 * Produce the Aptos address shape that Aptos JSON RPC returns inside event-type
 * strings (leading-zero-byte-trimmed). Used to build the event-type filter that
 * compares against `tx.events[].type` returned by the RPC. The legacy regex
 * stripped ALL leading zero hex chars and corrupted addresses like `0x…00aa`
 * to `0xaa`; this delegates to the SDK so byte boundaries are respected.
 *
 * NOTE: Opposite output shape from the server-side `canonicalAptosAddress` at
 * `typescript/server/src/utils/crypto.ts:80`, which returns long-form for
 * canonical-both-sides comparison. This helper is for matching RPC's wire
 * form when the local side is interpolated into a string template.
 */
export function aptosCanonicalAddress(address: string): string {
  return AccountAddress.from(address).toStringShort();
}

const SCAN_DAYS = 3;
const SCAN_WINDOW_S = SCAN_DAYS * 24 * 60 * 60;
const SCAN_WINDOW_MS = SCAN_DAYS * 24 * 60 * 60 * 1000;
const APTOS_TX_LIMIT = 100;
const SOLANA_SCAN_LIMIT = 100;
const MAX_ANKR_PAGES = 10;

/** CCTP V1 TokenMessenger contract addresses on mainnet (lowercased for comparison) */
const TOKEN_MESSENGER_ADDRESSES: Record<string, string> = {
  Ethereum: "0xbd3fa81b58ba92a82136038b25adec7066af3155",
  Arbitrum: "0x19330d10d9cc8751218eaf51e8885d058642e08a",
  Base: "0x1682ae6375c4e4a97e4b583bc394c861a46d8962",
};

/** Ankr blockchain identifiers for ankr_getTransactionsByAddress */
const ANKR_BLOCKCHAIN_IDS: Record<CctpNetwork, Record<string, string>> = {
  mainnet: { Ethereum: "eth", Arbitrum: "arbitrum", Base: "base" },
  // Ankr doesn't index Arbitrum Sepolia.
  testnet: { Ethereum: "eth_sepolia", Base: "base_sepolia" },
};

/** USDC mint address on Solana mainnet */
const SOLANA_USDC_MINT = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnkrTransaction {
  hash: string;
  // Ankr omits `to` for contract-creation transactions
  to?: string;
}

interface AnkrGetTransactionsResult {
  transactions: AnkrTransaction[];
  nextPageToken?: string;
}

interface SolanaSignatureInfo {
  signature: string;
  blockTime: number | null;
  err: unknown;
}

interface SolanaTokenAccount {
  pubkey: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function jsonRpc<T>(url: string, method: string, params: unknown[]): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  if (!response.ok) throw new Error(`RPC error: ${response.status}`);
  const data = (await response.json()) as { result?: T; error?: { message?: string } };
  if (data.error) throw new Error(data.error.message ?? "RPC error");
  return data.result as T;
}

/** Ankr Advanced API multichain endpoint. Requires NEXT_PUBLIC_ANKR_API_KEY. */
function getAnkrMultichainUrl(): string | null {
  const key = env.NEXT_PUBLIC_ANKR_API_KEY;
  if (!key) return null;
  return `https://rpc.ankr.com/multichain/${key}`;
}

// ---------------------------------------------------------------------------
// EVM discovery — account transactions via Ankr Advanced API
// ---------------------------------------------------------------------------

/**
 * Fetches recent transactions for the given account on an EVM chain using
 * ankr_getTransactionsByAddress, then filters for ones sent to a CCTP
 * TokenMessenger contract. Returns unique source transaction hashes.
 *
 * v1 is always scanned on mainnet; v2 on the app's network, which on testnet is a separate chain.
 */
export async function fetchEVMDepositForBurnTxHashes(
  chainName: string,
  depositorAddress: string,
  sinceTimestamp?: number,
): Promise<string[]> {
  const v1TokenMessenger = TOKEN_MESSENGER_ADDRESSES[chainName];
  const ankrUrl = getAnkrMultichainUrl();
  if (!v1TokenMessenger || !ankrUrl) return [];

  const network = appCctpNetwork();
  const v2TokenMessenger = EVM_TOKEN_MESSENGER_V2[network].toLowerCase();
  const scans = new Map<string, Set<string>>();
  const addScan = (blockchain: string | undefined, tokenMessenger: string) => {
    if (blockchain) scans.set(blockchain, (scans.get(blockchain) ?? new Set()).add(tokenMessenger));
  };
  addScan(ANKR_BLOCKCHAIN_IDS.mainnet[chainName], v1TokenMessenger);
  addScan(ANKR_BLOCKCHAIN_IDS[network][chainName], v2TokenMessenger);

  const fromTimestamp = sinceTimestamp
    ? Math.floor(sinceTimestamp / 1000)
    : Math.floor(Date.now() / 1000) - SCAN_DAYS * 24 * 60 * 60;

  const txHashes = new Set<string>();
  for (const [blockchain, tokenMessengers] of scans) {
    for (const hash of await fetchAnkrTxHashesTo(
      ankrUrl,
      blockchain,
      depositorAddress,
      fromTimestamp,
      tokenMessengers,
    )) {
      txHashes.add(hash);
    }
  }
  return Array.from(txHashes);
}

async function fetchAnkrTxHashesTo(
  ankrUrl: string,
  blockchain: string,
  depositorAddress: string,
  fromTimestamp: number,
  tokenMessengers: Set<string>,
): Promise<string[]> {
  const txHashes: string[] = [];
  let pageToken: string | undefined;
  let pages = 0;

  // Paginate through results (Ankr caps pages at ~10k items)
  do {
    const params: Record<string, unknown> = {
      blockchain,
      address: [depositorAddress],
      fromTimestamp,
      descOrder: true,
      pageSize: 100,
    };
    if (pageToken) params.pageToken = pageToken;

    const result = await jsonRpc<AnkrGetTransactionsResult>(
      ankrUrl,
      "ankr_getTransactionsByAddress",
      [params],
    );

    for (const tx of result.transactions) {
      if (tx.to && tokenMessengers.has(tx.to.toLowerCase())) txHashes.push(tx.hash);
    }

    pageToken = result.nextPageToken;
    pages++;
  } while (pageToken && pages < MAX_ANKR_PAGES);

  return txHashes;
}

// ---------------------------------------------------------------------------
// Solana discovery — recent USDC signatures
// ---------------------------------------------------------------------------

/**
 * Finds the user's USDC Associated Token Account (ATA) on Solana,
 * then fetches recent signatures for that account. This narrows results
 * to only USDC-related transactions instead of all wallet activity.
 */
export async function fetchSolanaRecentSignatures(
  solanaAddress: string,
  sinceTimestamp?: number,
): Promise<string[]> {
  const tokenAccounts = await jsonRpc<{ value: SolanaTokenAccount[] }>(
    RPC_URLS.Solana,
    "getTokenAccountsByOwner",
    [solanaAddress, { mint: SOLANA_USDC_MINT }, { encoding: "jsonParsed" }],
  );

  const firstAccount = tokenAccounts.value[0];
  if (!firstAccount) return [];

  const signatures = await jsonRpc<SolanaSignatureInfo[]>(
    RPC_URLS.Solana,
    "getSignaturesForAddress",
    [firstAccount.pubkey, { limit: SOLANA_SCAN_LIMIT }],
  );

  const cutoffTime = sinceTimestamp
    ? Math.floor(sinceTimestamp / 1000)
    : Math.floor(Date.now() / 1000) - SCAN_WINDOW_S;

  const filtered = signatures
    .filter((sig) => !sig.err && sig.blockTime && sig.blockTime > cutoffTime)
    .map((sig) => sig.signature);

  return filtered;
}

// ---------------------------------------------------------------------------
// Aptos discovery — recent CCTP burn transactions (MessageSent events)
// ---------------------------------------------------------------------------

/**
 * Fetches recent Aptos transactions that emitted a CCTP MessageSent event
 * (i.e. deposit_for_burn / withdrawal from Aptos). Scans the last ~100
 * transactions within a 3-day window.
 */
export async function fetchAptosDepositForBurnTxHashes(
  aptosAddress: string,
  sinceTimestamp?: number,
): Promise<string[]> {
  const aptos = readDexSdk.deps.aptos;

  const v1MessageTransmitter = getMessageTransmitterContract(APTOS_CCTP_DOMAIN_ID);
  if (!v1MessageTransmitter) return [];

  const messageSentEventTypes = new Set(
    [v1MessageTransmitter, APTOS_MESSAGE_TRANSMITTER_V2[appCctpNetwork()]].map(
      (address) => `${aptosCanonicalAddress(address)}::message_transmitter::MessageSent`,
    ),
  );

  // Fetch the latest transactions by computing offset from sequence number
  const accountInfo = await aptos.getAccountInfo({ accountAddress: aptosAddress });
  const seqNum = Number(accountInfo.sequence_number);
  const offset = Math.max(0, seqNum - APTOS_TX_LIMIT);

  const transactions = await aptos.getAccountTransactions({
    accountAddress: aptosAddress,
    options: { offset, limit: APTOS_TX_LIMIT },
  });

  // Aptos timestamps are in microseconds
  const cutoffMicros = sinceTimestamp
    ? sinceTimestamp * 1000
    : (Date.now() - SCAN_WINDOW_MS) * 1000;
  const txHashes: string[] = [];

  for (const tx of transactions) {
    if (tx.type !== TransactionResponseType.User) continue;
    if (!tx.success) continue;
    if (Number(tx.timestamp) < cutoffMicros) continue;

    const hasMessageSent = tx.events.some((e) => messageSentEventTypes.has(e.type));
    if (hasMessageSent) txHashes.push(tx.hash);
  }

  return txHashes;
}

// ---------------------------------------------------------------------------
// Unified discovery entry point
// ---------------------------------------------------------------------------

/**
 * Discovers candidate CCTP transaction hashes for the given source chain
 * and wallet address. Dispatches to the appropriate chain-specific discovery.
 *
 * @param sinceTimestamp - If provided, only returns transactions after this
 *   timestamp (ms). Falls back to the default per-chain window when omitted.
 */
export async function fetchCCTPCandidateTxHashes(
  chainName: string,
  sourceAddress: string,
  sinceTimestamp?: number,
): Promise<string[]> {
  if (chainName === "Aptos") {
    return fetchAptosDepositForBurnTxHashes(sourceAddress, sinceTimestamp);
  }
  if (chainName === "Solana") {
    return fetchSolanaRecentSignatures(sourceAddress, sinceTimestamp);
  }
  if (isEvmChain(chainName)) {
    return fetchEVMDepositForBurnTxHashes(chainName, sourceAddress, sinceTimestamp);
  }
  return [];
}
